import {
  setAuthData,
  getAccessToken,
  getRefreshToken,
  getAccessExpiration,
  getRefreshExpiration
} from '@/redux/auth';

import Store from '@/redux';
import type { TokenAuthDto } from '@AI/api.interfaces';
import { type UserState, getUser } from '@/redux/user';
import { setCampaignServerTime } from '@/redux/campaign';

export default abstract class Fetch {
  private readonly headers = new Headers({
    'x-api-key': import.meta.env.VITE_API_KEY as string,
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json'
  });

  private readonly options: RequestInit = {
    credentials: 'include',
    headers: this.headers
  };

  private readonly requests: Map<string, number> = new Map();
  protected abstract login(user: UserState): Promise<unknown>;

  private readonly baseURL = `${import.meta.env.VITE_API_BASE_URL}/api`;
  private readonly anonymousUrls = ['/auth/token/refresh', '/auth/signin'];

  protected startedRequests(...requests: string[]): boolean {
    for (let r = requests.length; r--; ) {
      if (this.requests.get(requests[r])) {
        return true;
      }
    }

    return false;
  }

  private async getReCaptchaHeaders(): Promise<Headers> {
    if (!(window as Application).grecaptcha) {
      if (import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY)
        throw new Error('ReCaptcha not initialized.');

      return this.headers;
    }

    try {
      const { enterprise } = (window as Application).grecaptcha as GreCaptcha;

      const token = await enterprise.execute(
        import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY as string,
        { action: 'post' }
      );

      const headers = new Headers(this.headers);
      headers.set('x-recaptcha-token', token);
      return headers;
    } catch {
      throw new Error('ReCaptcha not fetched.');
    }
  }

  private updateRequests(url = '', add = false): void {
    let pi = url.indexOf('?');
    pi = pi < 0 ? url.length : pi;
    const baseUrl = url.slice(0, pi);

    const current = this.requests.get(baseUrl) ?? 0;
    this.requests.set(baseUrl, Math.max(current + +add * 2 - 1, 0));
  }

  protected getAccessExpiration(delay = 0): number {
    return Date.now() + delay * 1e3;
  }

  private async updateExpiredToken(
    state = Store.getState(),
    now = Date.now()
  ): Promise<void> {
    // Force premature expiration if token will expire in 5 seconds:
    const accessExpiration = (getAccessExpiration(state) ?? now) - 5e3;
    const refreshExpiration = (getRefreshExpiration(state) ?? now) - 5e3;

    if (refreshExpiration <= now) {
      await this.login(getUser(state));
    } else if (accessExpiration <= now) {
      await this.refreshToken(getRefreshToken(state));
    }
  }

  private async fetch<Response>(
    url: string,
    options?: RequestInit
  ): Promise<Response> {
    this.updateRequests(url, true);

    if (!this.anonymousUrls.includes(url)) {
      await this.updateExpiredToken();
      const accessToken = getAccessToken(Store.getState());
      accessToken && this.headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const response = await fetch(`${this.baseURL}/${url}`, {
      ...this.options,
      ...options
    });

    this.updateRequests(url);

    if (
      response.type === 'error' ||
      (400 <= response.status && response.status < 600)
    )
      return Promise.reject(
        new Error(response.statusText, { cause: response.status })
      );

    const date = response.headers.get('date');
    Store.dispatch(setCampaignServerTime(date));

    return response.json() as Promise<Response>;
  }

  protected async patch<Response>(
    url: string,
    params?: object,
    options: RequestInit = {}
  ): Promise<Response> {
    return this.fetch<Response>(
      url,
      await this.getOptions('PATCH', params, options)
    );
  }

  protected async post<Response>(
    url: string,
    params?: object,
    options: RequestInit = {}
  ): Promise<Response> {
    return this.fetch<Response>(
      url,
      await this.getOptions('POST', params, options)
    );
  }

  protected async get<Response>(
    url: string,
    params?: object,
    options: RequestInit = {}
  ): Promise<Response> {
    const query = new URLSearchParams(params as Record<string, string>);
    url += (query.size && `?${query.toString()}`) || '';
    return this.fetch<Response>(
      url,
      await this.getOptions('GET', void 0, options)
    );
  }

  private async refreshToken(token: string | null): Promise<TokenAuthDto | void> {
    const data = await this.post<TokenAuthDto>('auth/token/refresh', {
      refreshToken: token
    });

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      data;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return data;
  }

  private async getOptions(
    method: 'DELETE' | 'PATCH' | 'POST' | 'PUT' | 'GET',
    params?: object,
    options: RequestInit = {}
  ): Promise<RequestInit> {
    return Object.assign(options, {
      method,
      ...(params && { body: JSON.stringify(params) }),
      ...(method !== 'GET' && { headers: await this.getReCaptchaHeaders() })
    });
  }
}
